<template>
  <div>
    <template v-if="needsJournalSelection">
      <v-alert
        v-if="journalsLoaded && !definitionJournals.length"
        type="warning"
        variant="tonal"
        class="mb-3">
        До типу документа не прив'язано жодного журналу реєстрації. Зверніться
        до адміністратора для прив'язки журналу через налаштування типу
        документа.
      </v-alert>

      <b-select
        v-else
        v-model="registrationCredentials.registrationJournalId"
        :error-messages="journalError ? [journalError] : []"
        :items="definitionJournals"
        item-title="name"
        item-value="id"
        label="Журнал реєстрації"
        class="mb-3"
        @update:model-value="onJournalChange" />
    </template>

    <b-label label="Спосіб присвоєння номера"></b-label>

    <v-row dense class="mb-3">
      <v-col v-for="item in modes" :key="item.value" cols="6">
        <v-card
          :color="
            registrationMode === item.value ? 'light-blue-lighten-2' : 'white'
          "
          :variant="registrationMode === item.value ? 'tonal' : 'flat'"
          :style="{
            borderColor: registrationMode === item.value ? '#01579b' : null,
            borderWidth: registrationMode === item.value ? '2px' : '1px',
          }"
          border
          class="h-100"
          rounded="lg"
          @click="registrationMode = item.value">
          <v-card-text class="pa-3">
            <div
              :class="
                registrationMode === item.value
                  ? 'text-light-blue-darken-4'
                  : 'text-high-emphasis'
              "
              class="font-weight-bold font-size-15">
              {{ item.title }}
            </div>
            <div class="text-body-2 text-medium-emphasis mt-1">
              {{ item.subtitle }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <div v-if="registrationMode === 'reserved'" class="mb-3">
      <b-label label="Оберіть зарезервований номер" class="mb-2"></b-label>

      <div
        class="d-flex flex-column ga-2 overflow-y-auto"
        style="max-height: 260px">
        <v-progress-linear
          v-if="$loading.isLoading('numberReservationList')"
          color="primary"
          indeterminate></v-progress-linear>

        <v-card
          v-for="item in reservedNumbers"
          :key="item.id"
          :color="
            registrationCredentials.registrationNumber ===
            item.registrationNumber
              ? 'deep-purple-lighten-5'
              : 'white'
          "
          :style="{
            border:
              registrationCredentials.registrationNumber ===
              item.registrationNumber
                ? '1.5px solid #7c3aed'
                : null,
          }"
          border
          rounded="lg"
          variant="flat"
          @click="
            registrationCredentials.registrationNumber = item.registrationNumber
          ">
          <v-card-text>
            <div class="text-deep-purple-accent-3 font-weight-bold text-h6">
              {{ item.registrationNumberDisplay || item.registrationNumber }}
            </div>
            <div class="text-body-2 text-medium-emphasis">
              Зарезервовано:
              {{ new CustomDate(item.reservedAt).toString() }} ·
              {{ getPersonName(item.reserved_by) || '—' }} · За:
              <span class="text-deep-purple-accent-3 font-weight-bold">
                {{ getPersonName(item.author) || '—' }}
              </span>
            </div>
          </v-card-text>
        </v-card>

        <v-card
          v-if="
            !$loading.isLoading('numberReservationList') &&
            !reservedNumbers.length
          "
          border
          class="border-dashed"
          rounded="lg"
          variant="flat">
          <v-card-text class="text-center text-medium-emphasis">
            Немає доступних зарезервованих номерів
          </v-card-text>
        </v-card>
      </div>
    </div>

    <b-input
      v-model="registrationCredentials.registrationNumber"
      :error-messages="getErrorMessages(v$.registrationNumber)"
      label="Реєстраційний номер"
      readonly
      @blur="v$.registrationNumber.$touch()"></b-input>

    <b-input-date
      v-model="registrationCredentials.registrationDate"
      label="Дата реєстрації" />
  </div>
</template>
<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { BInputDate, BLabel } from 'best-modules/components'
import { Dialog } from '@/dialog'
import { Document } from '@/utils/types/document'
import { NumberReservation } from '@/utils/types/documentRegistration'
import { documentManualRegister } from '@/requests/document'
import {
  numberReservationList,
  numberReservationPreview,
} from '@/requests/document-registration'
import { required, useVuelidate } from '@/plugins'
import { getErrorMessages } from '@/utils/helpers'
import { CustomDate, getPersonName } from 'best-modules/utils'
import { ISODate } from 'best-modules/utils/customDate'
import { registrationJournalsByDefinition } from '@/requests/registrationJournal'

type RegistrationMode = 'auto' | 'reserved'

defineOptions({
  name: 'DocumentRegistration',
})

const props = defineProps<{
  dialog: Dialog
}>()

const document = props.dialog.dialogData.document as Document

const needsJournalSelection = !document.registrationJournalId

const registrationMode = ref<RegistrationMode>('auto')
const reservedNumbers = ref<NumberReservation[]>([])
const journalError = ref<string | null>(null)
const journalsLoaded = ref(false)
const definitionJournals = ref<{ id: number; name: string }[]>([])

const registrationCredentials = reactive<{
  registrationJournalId: number | null
  registrationNumber: string
  registrationDate: ISODate
}>({
  registrationJournalId: document.registrationJournalId ?? null,
  registrationNumber: '',
  registrationDate: new CustomDate(new Date()).toString({
    format: 'iso',
  }) as ISODate,
})

const modes = computed(() => [
  {
    value: 'auto' as RegistrationMode,
    title: 'Автоматично з журналу',
    subtitle: 'Наступний доступний номер',
  },
  {
    value: 'reserved' as RegistrationMode,
    title: 'Зарезервований номер',
    subtitle: `${reservedNumbers.value.length} доступн. номер(ів)`,
  },
])

const v$ = useVuelidate(
  { registrationNumber: { required } },
  registrationCredentials
)

const currentJournalId = () =>
  registrationCredentials.registrationJournalId ??
  document.registrationJournalId

const loadPreview = async (): Promise<string> => {
  const res = await numberReservationPreview(currentJournalId())
  return res.registrationNumber
}

const loadReservedNumbers = async () => {
  if (!currentJournalId()) return
  reservedNumbers.value = []
  const res = await numberReservationList({
    registrationJournalId: currentJournalId(),
    status: 'reserved',
  })
  reservedNumbers.value = res.data
}

const onJournalChange = async () => {
  journalError.value = null
  registrationCredentials.registrationNumber = ''
  v$.value.$reset()
  await loadReservedNumbers()
  if (registrationMode.value === 'auto') {
    registrationCredentials.registrationNumber = await loadPreview()
  }
}

const submit = () => {
  if (
    needsJournalSelection &&
    journalsLoaded.value &&
    !definitionJournals.value.length
  ) {
    return Promise.reject(new Error('no journals available'))
  }
  if (needsJournalSelection && !registrationCredentials.registrationJournalId) {
    journalError.value = 'Оберіть журнал реєстрації'
    return Promise.reject(new Error('journal required'))
  }
  return documentManualRegister(document.id, {
    ...registrationCredentials,
    registrationNumber:
      registrationMode.value === 'auto'
        ? null
        : registrationCredentials.registrationNumber,
  })
}

watch(
  registrationMode,
  async value => {
    v$.value.$reset()

    if (!currentJournalId()) return

    if (value === 'auto') {
      registrationCredentials.registrationNumber = await loadPreview()
    }

    if (value === 'reserved' && reservedNumbers.value.length) {
      registrationCredentials.registrationNumber = null as any
      if (reservedNumbers.value.length) {
        registrationCredentials.registrationNumber =
          reservedNumbers.value[0].registrationNumber
      }
    }
  },
  { immediate: true }
)

if (!needsJournalSelection) {
  loadReservedNumbers()
} else {
  registrationJournalsByDefinition(document.docTypeDefinitionId).then(res => {
    definitionJournals.value = res
    journalsLoaded.value = true

    const preferred =
      res.find(j => j.id === document.registrationJournalId) ?? res[0]
    if (preferred) {
      registrationCredentials.registrationJournalId = preferred.id
      onJournalChange()
    }
  })
}

defineExpose({ v$, submit })
</script>
